import { useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Spinner,
  Alert,
  Button,
  Badge,
  Form,
  Image,
} from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import api from "@/api/api.js";
import { ROLES } from "@/config/roles.js";
import ConstanciaActions from "@/pages/shared/sections/ConstanciaActions.jsx";
import FielDownload from "@/pages/shared/sections/FielDownload.jsx";
import ReceptorDetail from "@/pages/shared/sections/ReceptorDetail.jsx";
import BillingAmounts from "@/pages/shared/sections/BillingAmounts.jsx";

const BILLING_STATUS_VARIANT = {
  1: "secondary",
  2: "success",
  3: "danger",
  4: "warning",
  5: "dark",
};

const BILLING_STATUS_REQUESTED = 1;
const BILLING_STATUS_REQUESTED_TO_CANCEL = 4;
const BILLING_STATUS_CANCELLED = 5;

const ShowBillAdmin = () => {
  const { billingId } = useParams();
  const navigate = useNavigate();
  const roles = useSelector((state) => state.auth.roles);

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [chatError, setChatError] = useState("");
  const [chatOk, setChatOk] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [supportingLoading, setSupportingLoading] = useState(false);
  const [supportingError, setSupportingError] = useState("");
  const [supportingImageUrl, setSupportingImageUrl] = useState("");

  const [zipDownloadLoading, setZipDownloadLoading] = useState(false);
  const [zipDownloadError, setZipDownloadError] = useState("");

  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  const [cancelBillingLoading, setCancelBillingLoading] = useState(false);
  const [cancelBillingError, setCancelBillingError] = useState("");

  const [zipFile, setZipFile] = useState(null);
  const [notifyFlag, setNotifyFlag] = useState(false);

  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(name || "");
  const isZip = (name) => /\.zip$/i.test(name || "");

  const fetchBill = async () => {
    if (!billingId) return;

    try {
      setRefreshing(true);
      setErrorMsg("");

      const { data } = await api.get(`/billings/${billingId}`);
      setBill(data);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo cargar la factura.";

      setErrorMsg(apiMessage);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!billingId) return;

      try {
        setLoading(true);
        setErrorMsg("");

        const { data } = await api.get(`/billings/${billingId}`);
        if (!isMounted) return;

        setBill(data);
      } catch (err) {
        if (!isMounted) return;

        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "No se pudo cargar la factura.";

        setErrorMsg(apiMessage);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [billingId]);

  useEffect(() => {
    let alive = true;

    const loadSupportingPreviewIfImage = async () => {
      setSupportingError("");
      if (supportingImageUrl) {
        URL.revokeObjectURL(supportingImageUrl);
        setSupportingImageUrl("");
      }

      const originalName = bill?.supportingDocumentOriginalName;
      if (!bill?.id || !bill?.supportingDocumentPath || !isImage(originalName))
        return;

      try {
        setSupportingLoading(true);

        const res = await api.get(`/billings/${bill.id}/supporting-document`, {
          responseType: "blob",
        });

        if (!alive) return;

        const blob = res.data;
        const url = URL.createObjectURL(blob);
        setSupportingImageUrl(url);
      } catch (err) {
        if (!alive) return;

        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "No se pudo cargar el documento soporte.";

        setSupportingError(apiMessage);
      } finally {
        if (alive) setSupportingLoading(false);
      }
    };

    loadSupportingPreviewIfImage();

    return () => {
      alive = false;
      if (supportingImageUrl) {
        URL.revokeObjectURL(supportingImageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bill?.id,
    bill?.supportingDocumentPath,
    bill?.supportingDocumentOriginalName,
  ]);

  const downloadSupportingDocument = async () => {
    if (!bill?.id || !bill?.supportingDocumentPath) return;

    try {
      setSupportingLoading(true);
      setSupportingError("");

      const res = await api.get(`/billings/${bill.id}/supporting-document`, {
        responseType: "blob",
      });

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = bill.supportingDocumentOriginalName || "supporting-document";
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo descargar el documento soporte.";

      setSupportingError(apiMessage);
    } finally {
      setSupportingLoading(false);
    }
  };

  const downloadZipDocument = async () => {
    if (!bill?.id || !bill?.zipFile) return;

    try {
      setZipDownloadLoading(true);
      setZipDownloadError("");

      const res = await api.get(`/billings/${bill.id}/zip-document`, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = bill.originalZipFile || "factura.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo descargar el archivo ZIP.";

      setZipDownloadError(apiMessage);
    } finally {
      setZipDownloadLoading(false);
    }
  };

  const resetBilling = async () => {
    if (!bill?.id) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "¿Resetear factura?",
      text: "La factura volverá al estado Solicitado.",
      showCancelButton: true,
      confirmButtonText: "Sí, resetear",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      setResetLoading(true);
      setResetError("");

      await api.post(`/billings/${bill.id}/reset`);
      navigate("/billsAdmin");
    } catch (err) {
      setResetError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "No se pudo resetear la factura.",
      );
    } finally {
      setResetLoading(false);
    }
  };

  const cancelBilling = async () => {
    if (!bill?.id) return;

    try {
      setCancelBillingLoading(true);
      setCancelBillingError("");

      const { data } = await api.patch(
        `/billings/${bill.id}`,
        { billingStatus: BILLING_STATUS_CANCELLED },
        { headers: { "Content-Type": "application/merge-patch+json" } },
      );

      setBill(data);
    } catch (err) {
      setCancelBillingError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "No se pudo cancelar la factura.",
      );
    } finally {
      setCancelBillingLoading(false);
    }
  };

  const createConversation = async (e) => {
    e.preventDefault();

    const body = (messageBody || "").trim();
    if (!body) return;

    if (!bill?.id) {
      setChatError("No se encontró la factura para enviar el comentario.");
      return;
    }

    try {
      setSending(true);
      setChatError("");
      setChatOk("");

      await api.post(`/billings/${bill.id}/conversations`, { body });

      setMessageBody("");
      setChatOk("Comentario enviado.");
      await fetchBill();
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo enviar el comentario.";

      setChatError(apiMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendZip = async () => {
    if (!zipFile) {
      Swal.fire({
        icon: "error",
        title: "Archivo requerido",
        text: "Debes seleccionar un archivo ZIP.",
      });
      return;
    }
    if (!isZip(zipFile.name)) {
      Swal.fire({
        icon: "error",
        title: "Formato inválido",
        text: "El archivo debe ser un ZIP.",
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: "question",
      title: "¿Enviar factura?",
      text: `Se enviará "${zipFile.name}"${notifyFlag ? " y se notificará al cliente." : "."}`,
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("file", zipFile);
      formData.append("notifyClient", notifyFlag ? "1" : "0");

      await api.post(`/billings/${bill.id}/upload-zip-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setZipFile(null);
      setNotifyFlag(false);
      await fetchBill();

      Swal.fire({
        icon: "success",
        title: "Enviado",
        text: "El archivo fue enviado correctamente.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo enviar el archivo.";

      Swal.fire({ icon: "error", title: "Error", text: msg });
    }
  };

  const conversations = Array.isArray(bill?.billingConversations)
    ? bill.billingConversations
    : [];
  const statusVariant = BILLING_STATUS_VARIANT[bill?.billingStatus] ?? "dark";

  if (loading) {
    return (
      <Row className="justify-content-center">
        <Col xs={12} md={8}>
          <Card>
            <Card.Header>Detalle de factura</Card.Header>
            <Card.Body className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Cargando...</span>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  if (errorMsg) {
    return (
      <Row className="justify-content-center">
        <Col xs={12} md={8}>
          <Alert variant="danger" className="mb-3">
            {errorMsg}
          </Alert>
          <Button variant="secondary" as={Link} to="/billsAdmin">
            Volver
          </Button>
        </Col>
      </Row>
    );
  }

  if (!bill) {
    return (
      <Row className="justify-content-center">
        <Col xs={12} md={8}>
          <Alert variant="warning">No se encontró información.</Alert>
          <Button variant="secondary" as={Link} to="/billsAdmin">
            Volver
          </Button>
        </Col>
      </Row>
    );
  }

  const currentUserId = sessionStorage.getItem("user");
  const canWork =
    bill?.workedBy?.id != null && bill.workedBy.id === currentUserId;
  const isSuperAdmin = roles?.includes(ROLES.SUPER_ADMIN);

  return (
    <Row className="justify-content-center g-3">
      <Col xs={12} lg={12}>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold">Detalle de factura</div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Folio: {safe(bill.id)} · Creada: {safe(bill.createdAtAgo)}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {bill.priority && (
                <Badge bg="warning" text="dark">
                  <i className="bi bi-lightning-charge-fill me-1" />
                  Atención prioritaria
                </Badge>
              )}
              <Badge bg={statusVariant}>{safe(bill.billingStatusLabel)}</Badge>
              <Button variant="secondary" as={Link} to="/billsAdmin">
                Volver
              </Button>
            </div>
          </Card.Header>

          {!canWork && (
            <div className="alert alert-warning mb-0 rounded-0">
              Solo lectura.{" "}
              {bill.workedBy
                ? `Esta factura la está trabajando ${bill.workedBy?.userProfile?.fullName ?? "otro contador"}.`
                : "Toma la factura con “Trabajar Factura” en la lista para subir documentos o enviar mensajes."}
            </div>
          )}

          <Card.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Card className="h-100">
                  <Card.Header>Solicitante</Card.Header>
                  <Card.Body>
                    <div>
                      <strong>Solicitado por:</strong>{" "}
                      {safe(bill?.requestedBy?.userProfile?.fullName)}
                    </div>
                    <div>
                      <strong>Ticket:</strong> {safe(bill?.requestTicket?.id)}
                    </div>
                    <div>
                      <strong>Ticket creado:</strong>{" "}
                      {safe(bill?.requestTicket?.createdAtAgo)}
                    </div>

                    <hr className="my-2" />
                    <div className="fw-bold mb-1">Empresa que factura</div>
                    <div>
                      <strong>Empresa:</strong> {safe(bill?.company?.name)}
                    </div>
                    <div>
                      <strong>RFC:</strong>{" "}
                      {safe(bill?.company?.taxIdentification)}
                    </div>
                    {bill?.company?.id && (
                      <>
                        <div className="mt-2">
                          <Link to={`/${bill.company.id}/detailCompany`}>
                            <i className="bi bi-box-arrow-up-right me-1" />
                            Ver detalle fiscal de la empresa
                          </Link>
                        </div>
                        {canWork ? (
                          <div className="mt-2 d-flex flex-column gap-2">
                            <ConstanciaActions companyId={bill.company.id} />
                            <FielDownload companyId={bill.company.id} />
                          </div>
                        ) : (
                          <div
                            className="text-muted mt-2"
                            style={{ fontSize: 13 }}
                          >
                            La constancia y la FIEL solo están disponibles para
                            el contador que está trabajando la factura.
                          </div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                {bill?.contact?.id ? (
                  <ReceptorDetail
                    contactId={bill.contact.id}
                    canDownload={canWork}
                  />
                ) : (
                  <Card className="h-100">
                    <Card.Header>Receptor (se factura a)</Card.Header>
                    <Card.Body>
                      <div>
                        <strong>Razón social:</strong> {safe(bill.businessName)}
                      </div>
                      <div>
                        <strong>RFC receptor:</strong>{" "}
                        {safe(bill.taxIdentification)}
                      </div>
                      <div>
                        <strong>Teléfono:</strong> {safe(bill.phone)}
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Col>

              <Col xs={12}>
                <Card>
                  <Card.Header>Domicilio fiscal</Card.Header>
                  <Card.Body>
                    <Row className="g-2">
                      <Col xs={12} md={4}>
                        <strong>Calle:</strong> {safe(bill.street)}
                      </Col>
                      <Col xs={6} md={2}>
                        <strong>Ext:</strong> {safe(bill.exterior)}
                      </Col>
                      <Col xs={6} md={2}>
                        <strong>Int:</strong> {safe(bill.interior)}
                      </Col>
                      <Col xs={12} md={4}>
                        <strong>Colonia:</strong> {safe(bill.suburb)}
                      </Col>

                      <Col xs={12} md={4}>
                        <strong>Municipio/Alcaldía:</strong> {safe(bill.town)}
                      </Col>
                      <Col xs={12} md={4}>
                        <strong>Estado:</strong> {safe(bill.state)}
                      </Col>
                      <Col xs={12} md={4}>
                        <strong>C.P.:</strong> {safe(bill.postalCode)}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="h-100">
                  <Card.Header>CFDI</Card.Header>
                  <Card.Body>
                    <div>
                      <strong>Uso CFDI:</strong> {safe(bill.cfdiUse)}
                    </div>
                    <div>
                      <strong>Método de pago:</strong> {safe(bill.paymentMode)}
                    </div>
                    <div>
                      <strong>Forma de pago:</strong> {safe(bill.paymentForm)}
                    </div>
                    <div>
                      <strong>Moneda:</strong> {safe(bill.currency)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="h-100">
                  <Card.Header>Archivos</Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Documento soporte:</strong>{" "}
                      {bill.supportingDocumentPath ? (
                        <>
                          {supportingError && (
                            <Alert variant="warning" className="mb-2 mt-2">
                              {supportingError}
                            </Alert>
                          )}

                          {isImage(bill.supportingDocumentOriginalName) ? (
                            <>
                              <div
                                className="text-muted"
                                style={{ fontSize: 12 }}
                              >
                                {safe(bill.supportingDocumentOriginalName)}
                              </div>

                              {supportingLoading ? (
                                <div className="text-center text-muted mt-2">
                                  Cargando imagen...
                                </div>
                              ) : supportingImageUrl ? (
                                <Image
                                  src={supportingImageUrl}
                                  fluid
                                  rounded
                                  className="mt-2"
                                  style={{
                                    maxHeight: "400px",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : null}

                              <div className="mt-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={downloadSupportingDocument}
                                  disabled={supportingLoading}
                                >
                                  {supportingLoading
                                    ? "Descargando..."
                                    : "Descargar"}
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span>
                                {safe(bill.supportingDocumentOriginalName)}
                              </span>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={downloadSupportingDocument}
                                disabled={supportingLoading}
                              >
                                {supportingLoading
                                  ? "Descargando..."
                                  : "Descargar"}
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </div>

                    <div className="mt-3">
                      <strong>Factura (ZIP):</strong>{" "}
                      {bill.zipFile ? (
                        <>
                          {zipDownloadError && (
                            <Alert variant="warning" className="mb-2 mt-2">
                              {zipDownloadError}
                            </Alert>
                          )}
                          <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                            <span>{safe(bill.originalZipFile)}</span>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={downloadZipDocument}
                              disabled={zipDownloadLoading}
                            >
                              {zipDownloadLoading
                                ? "Descargando..."
                                : "Descargar"}
                            </Button>
                          </div>
                        </>
                      ) : (
                        "-"
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12}>
                <BillingAmounts bill={bill} />
              </Col>

              <Col xs={12}>
                <Card>
                  <Card.Header>Notas</Card.Header>
                  <Card.Body>
                    <div>{safe(bill.notes)}</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12}>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="fw-bold">Chat</div>
            <div className="d-flex gap-2 align-items-center">
              <Badge bg="secondary">Mensajes: {conversations.length}</Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchBill}
                disabled={refreshing}
              >
                {refreshing ? "Actualizando..." : "Actualizar"}
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            {chatError && (
              <Alert variant="danger" className="mb-3">
                {chatError}
              </Alert>
            )}
            {chatOk && (
              <Alert variant="success" className="mb-3">
                {chatOk}
              </Alert>
            )}

            <div
              style={{
                maxHeight: 360,
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: 8,
                padding: 12,
                background: "#fafafa",
              }}
              className="mb-3"
            >
              {conversations.length === 0 ? (
                <div className="text-muted">Aún no hay mensajes.</div>
              ) : (
                conversations.map((c) => {
                  const authorName =
                    c?.author?.userProfile?.fullName ||
                    c?.author?.fullName ||
                    c?.authorName ||
                    "Usuario";

                  const createdAt = c?.createdAtAgo || c?.createdAt || "-";
                  const cid =
                    c?.id ?? c?.["@id"] ?? `${authorName}-${createdAt}`;

                  return (
                    <div key={cid} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex flex-column">
                          <strong>{authorName}</strong>
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            {createdAt}
                          </span>
                        </div>
                      </div>

                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {safe(c?.body)}
                      </div>
                      <hr className="my-2" />
                    </div>
                  );
                })
              )}
            </div>

            <Form onSubmit={createConversation}>
              <Form.Group className="mb-2" controlId="chatBody">
                <Form.Label>Nuevo comentario</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="Escribe aquí..."
                  disabled={sending || !canWork}
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={sending || !messageBody.trim() || !canWork}
                >
                  {sending ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>

      {bill.billingStatus === BILLING_STATUS_REQUESTED && canWork && (
        <Col xs={12}>
          <Card>
            <Card.Header>Enviar factura / documento</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Archivo ZIP</Form.Label>
                <Form.Control
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
                />
                {zipFile && (
                  <Form.Text className="text-muted">
                    Seleccionado: {zipFile.name}
                  </Form.Text>
                )}
              </Form.Group>
              <p className="text-muted small mb-3">
                <i className="bi bi-info-circle me-1"></i>
                Al enviar, se notificará automáticamente al cliente por correo.
              </p>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={handleSendZip}>
                  Enviar
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}

      {bill.billingStatus === BILLING_STATUS_REQUESTED && isSuperAdmin && (
        <Col xs={12}>
          <Card>
            <Card.Header>Acciones</Card.Header>
            <Card.Body>
              {resetError && (
                <Alert variant="danger" className="mb-3">
                  {resetError}
                </Alert>
              )}
              <div className="d-flex gap-3 mt-2">
                <Button
                  variant="outline-secondary"
                  onClick={resetBilling}
                  disabled={resetLoading}
                >
                  {resetLoading ? "Liberando..." : "Liberar folio"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}

      {bill.billingStatus === BILLING_STATUS_REQUESTED_TO_CANCEL && (
        <Col xs={12}>
          <Card>
            <Card.Header>Cancelación solicitada</Card.Header>
            <Card.Body>
              {resetError && (
                <Alert variant="danger" className="mb-3">
                  {resetError}
                </Alert>
              )}
              {cancelBillingError && (
                <Alert variant="danger" className="mb-3">
                  {cancelBillingError}
                </Alert>
              )}
              <div className="d-flex gap-3 mt-2">
                {isSuperAdmin && !bill.zipFile && (
                  <Button
                    variant="secondary"
                    onClick={resetBilling}
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Liberando..." : "Liberar folio"}
                  </Button>
                )}
                {bill.zipFile && (
                  <Button
                    variant="danger"
                    onClick={cancelBilling}
                    disabled={cancelBillingLoading}
                  >
                    {cancelBillingLoading
                      ? "Cancelando..."
                      : "Cancelar factura"}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default ShowBillAdmin;
